import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DateTime } from "luxon";
import {
    Box,
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    IconButton,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Collapse,
    Stepper,
    Step,
    StepButton,
    StepContent,
    Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import AssignToField from "./AssignToField";
import ManualChapterPicker from "./ManualChapterPicker";
import { nyDateInput } from "./prepDates";
import {
    CADENCES, CADENCE_LABELS, DOW, HOMEWORK_KINDS, MAX_ROUNDS,
    WeaknessSourceField, cadenceNeedsDays, cadenceSummary, fmtHm,
} from "./prepRoundShared";

// Step-based creator for homework rounds ("Add round") and recurring rules ("Set up
// recurring rounds"), also used to EDIT a recurring rule. Vertical stepper: finished steps
// collapse to a one-line summary and stay clickable, so every choice is reviewable at a
// glance before anything is created.
//
// Step order is Kind → When → Chapters → Who, deliberately: for a recurring rule the
// chapters step can assign DIFFERENT chapters to each generated round (Phase 3b round_plan),
// so the schedule must exist first — the grid's rows ARE the schedule's dates, served by the
// server preview endpoint (the same generator that will materialize, so keys can't drift).

const KIND_DEFAULTS = {
    num_chapters: 2, questions_per_chapter: 30, mastery_threshold: 100, mastery_cycles: 2,
    source_mode: "ALL", source_exam_ids: [], source_from_date: "", source_to_date: "",
    fallback_to_priority: true, assign_mode: "ALL", student_ids: [],
    notify_teachers_on_due: false,
    question_mode: "ADAPTIVE", set_size: 10, set_scope: "COHORT", manual_chapters: [],
};

const freshRoundData = () => {
    // Office-decided prefills: release 9:00 AM ET, due end of day — rolled to TOMORROW when
    // today's 9:00 already passed (the server rejects past release datetimes).
    const nowNy = DateTime.now().setZone("America/New_York");
    const day = (nowNy.hour >= 9 ? nowNy.plus({ days: 1 }) : nowNy).toFormat("yyyy-MM-dd");
    return { ...KIND_DEFAULTS, release_date: `${day}T09:00`, due_date: `${day}T23:59` };
};

const freshRecurrenceData = () => ({
    ...KIND_DEFAULTS,
    cadence: "WEEKLY", release_days_of_week: [1], release_time: "09:00", due_time: "23:59",
    due_offset_days: 6, start_date: "", end_date: "",
});

const ruleToData = (rule) => ({
    cadence: rule.cadence || "WEEKLY",
    release_days_of_week: Array.isArray(rule.release_days_of_week) && rule.release_days_of_week.length
        ? rule.release_days_of_week
        : (rule.release_day_of_week != null ? [rule.release_day_of_week] : []),
    // Legacy NULL times show what the rule actually does today: 09:10 / end of day.
    release_time: rule.release_time || "09:10",
    due_time: rule.due_time || "23:59",
    due_offset_days: rule.due_offset_days ?? 6,
    num_chapters: rule.num_chapters ?? 2,
    questions_per_chapter: rule.questions_per_chapter ?? "",
    mastery_threshold: rule.mastery_threshold ?? 100,
    mastery_cycles: rule.mastery_cycles ?? 2,
    start_date: nyDateInput(rule.start_date),
    end_date: nyDateInput(rule.end_date),
    source_mode: rule.source_mode || "ALL",
    source_exam_ids: rule.source_exam_ids || [],
    source_from_date: nyDateInput(rule.source_from_date),
    source_to_date: nyDateInput(rule.source_to_date),
    fallback_to_priority: rule.fallback_to_priority ?? true,
    assign_mode: rule.assign_mode || "ALL",
    student_ids: rule.student_ids || [],
    notify_teachers_on_due: rule.notify_teachers_on_due === true,
    question_mode: rule.question_mode || "ADAPTIVE",
    set_size: rule.set_size ?? 10,
    set_scope: rule.set_scope || "COHORT",
    manual_chapters: (rule.manual_chapters || []).map((m) => ({ chapter_id: m.chapter_id, total_questions: m.total_questions ?? undefined })),
});

// Stored plan entry ({date, chapters:[snapshots]}) → the picker's value shape.
const planToByDate = (rule) => {
    const out = {};
    (rule.round_plan || []).forEach((e) => {
        out[e.date] = (e.chapters || []).map((m) => ({ chapter_id: m.chapter_id, total_questions: m.total_questions ?? undefined }));
    });
    return out;
};

// The overlap/starvation warning with a concrete, expandable walk-through built from the
// admin's OWN numbers (window length, cadence, chapters per round) — the mechanism is much
// easier to believe as a played-out week than as a sentence.
const StarvationWarning = ({ text, cadence, dueOffsetDays, chaptersPerRound }) => {
    const [showExample, setShowExample] = useState(false);
    const n = Number(dueOffsetDays) || 0;                 // due window length, days
    const step = cadence === "EVERY_2_DAYS" ? 2 : 1;      // days between releases
    const per = Number(chaptersPerRound) || 2;            // chapters each round tries to pick
    const overlap = Math.floor(n / step);                 // earlier rounds still open at a release

    const rows = [];
    for (let k = 0; k <= Math.min(overlap, 3); k += 1) {
        const day = k * step + 1;
        const locked = Math.min(k, overlap) * per;
        if (k === 0) {
            rows.push([`Day ${day}`, `Round 1 picks the ${per} weakest chapters — and stays open ${n} day${n === 1 ? "" : "s"}.`]);
        } else if (k < overlap) {
            rows.push([`Day ${day}`, `Round ${k + 1} can't reuse the ${locked} chapter${locked === 1 ? "" : "s"} still locked by the open round${k === 1 ? "" : "s"} above → must find ${per} more.`]);
        } else {
            rows.push([`Day ${day}`, `Round ${k + 1} releases with ${locked} chapters locked at once ⚠ — a student without ${locked + per} practicable chapters left gets NOTHING today (recorded as "no practice data" and retried nightly).`]);
        }
    }

    return (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
            {text}
            <Box>
                <Button size="small" color="warning" sx={{ mt: 0.5, px: 0.5 }} onClick={() => setShowExample((v) => !v)}>
                    {showExample ? "Hide example" : "Show an example"}
                </Button>
            </Box>
            <Collapse in={showExample}>
                <Box sx={{ mt: 0.5, pl: 1, borderLeft: "2px solid", borderColor: "warning.main" }}>
                    {rows.map(([day, line]) => (
                        <Typography key={day} variant="caption" sx={{ display: "block", mb: 0.25 }}>
                            <b>{day}:</b> {line}
                        </Typography>
                    ))}
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                        Every open round locks its chapters so one study session can't count toward two
                        rounds. With a <b>0–{step} day</b> window each round closes before the pile-up
                        starts — chapters unlock in time and every round has material.
                    </Typography>
                </Box>
            </Collapse>
        </Alert>
    );
};

const RoundWizard = ({ open, onClose, mode, editRule, programUuid, subjectCourse, programExams, qbType, onSaved }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}`;
    const auth = { headers: { Authorization: getAuthHeader() } };
    const isRule = mode === "recurrence";

    const [step, setStep] = useState(0);
    const [data, setData] = useState(freshRoundData);
    const [sameForAll, setSameForAll] = useState(true);
    const [planByDate, setPlanByDate] = useState({});
    const [preview, setPreview] = useState({ rounds: [], over_cap: false, warning: null, loading: false });
    const [rowEditor, setRowEditor] = useState(null); // date being edited in the per-round dialog
    const [rowDraft, setRowDraft] = useState([]);     // staged picks (committed on Save, per UX rule)
    const [chapterNames, setChapterNames] = useState(new Map());
    const [formError, setFormError] = useState("");
    const [busy, setBusy] = useState(false);

    const set = (patch) => setData((prev) => ({ ...prev, ...patch }));

    useEffect(() => {
        if (!open) return;
        setStep(0);
        setFormError("");
        if (isRule && editRule) {
            setData(ruleToData(editRule));
            const byDate = planToByDate(editRule);
            setPlanByDate(byDate);
            setSameForAll(Object.keys(byDate).length === 0);
        } else if (isRule) {
            setData(freshRecurrenceData());
            setPlanByDate({});
            setSameForAll(true);
        } else {
            setData(freshRoundData());
            setPlanByDate({});
            setSameForAll(true);
        }
    }, [open, isRule, editRule]);

    // Chapter titles for the per-round grid's chips and the summaries. NOTE: the taxonomy
    // route is NOT program-scoped — same URL ManualChapterPicker uses.
    useEffect(() => {
        if (!open || !qbType || data.question_mode === "ADAPTIVE") return;
        Axios.get(`${process.env.REACT_APP_URL}/api/v1/prep-programs/taxonomy?qb_type=${qbType}`, auth)
            .then((res) => {
                const names = new Map();
                (res.data || []).forEach((s) => (s.chapters || []).forEach((ch) => names.set(ch.id, ch.title)));
                setChapterNames(names);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, qbType, data.question_mode]);

    // Server-side schedule preview (recurring only) — debounced; the SAME generator the save
    // will use, so the grid's date keys always match what materializes.
    useEffect(() => {
        if (!open || !isRule) return undefined;
        if (!data.start_date || !data.end_date) {
            setPreview({ rounds: [], over_cap: false, warning: null, loading: false });
            return undefined;
        }
        setPreview((p) => ({ ...p, loading: true }));
        const t = setTimeout(() => {
            Axios.post(`${base}/homework-recurrences/preview`, {
                // The server's starvation warning is mode-aware (FIXED_SET is exempt — it has
                // no open-round chapter exclusion), so the preview has to say which mode it is.
                question_mode: data.question_mode,
                cadence: data.cadence,
                release_days_of_week: cadenceNeedsDays(data.cadence) ? data.release_days_of_week : [],
                release_time: data.release_time || null,
                due_time: data.due_time || null,
                due_offset_days: Number(data.due_offset_days),
                start_date: data.start_date,
                end_date: data.end_date,
            }, auth)
                .then((res) => setPreview({ ...res.data, loading: false }))
                .catch(() => setPreview({ rounds: [], over_cap: false, warning: null, loading: false }));
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isRule, data.question_mode, data.cadence, JSON.stringify(data.release_days_of_week),
        data.release_time, data.due_time, data.due_offset_days, data.start_date, data.end_date]);

    const futureRounds = useMemo(() => (preview.rounds || []).filter((r) => !r.is_past), [preview.rounds]);

    const picksLabel = useCallback((picks) => {
        if (!picks || !picks.length) return null;
        const names = picks.map((p) => chapterNames.get(p.chapter_id) || `Ch ${p.chapter_id}`);
        return names.length <= 2 ? names.join(", ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
    }, [chapterNames]);

    // ---- step validity -------------------------------------------------------------------
    const scheduleOk = isRule
        ? !!data.start_date && !!data.end_date && !preview.over_cap && !preview.loading
            && futureRounds.length > 0
            && (!cadenceNeedsDays(data.cadence) || (data.release_days_of_week?.length || 0) > 0)
        : !!data.release_date && !!data.due_date;

    const gridComplete = futureRounds.every((r) => (planByDate[r.date]?.length || 0) > 0);
    const chaptersOk = data.question_mode === "ADAPTIVE"
        || (data.question_mode === "FIXED_SET" && !(Number(data.set_size) > 0) ? false
            : (!isRule || sameForAll ? (data.manual_chapters?.length || 0) > 0 : gridComplete));

    const whoOk = !(data.assign_mode === "SELECTED" && (data.student_ids?.length || 0) === 0)
        && !(data.source_mode === "EXAMS" && data.question_mode === "ADAPTIVE" && data.source_exam_ids.length === 0);

    // ---- save ------------------------------------------------------------------------------
    const save = async () => {
        setBusy(true);
        setFormError("");
        const modePayload = {
            question_mode: data.question_mode,
            mastery_cycles: Number(data.mastery_cycles) || 2,
            ...(data.question_mode === "FIXED_SET"
                ? { set_size: Number(data.set_size), set_scope: data.set_scope, sampling: "RANDOM" }
                : {}),
        };
        try {
            if (!isRule) {
                await Axios.post(`${base}/homework-rounds`, {
                    prep_program_course_id: subjectCourse.id,
                    release_date: data.release_date || null,
                    due_date: data.due_date || null,
                    num_chapters: Number(data.num_chapters),
                    questions_per_chapter: data.questions_per_chapter === "" ? null : Number(data.questions_per_chapter),
                    mastery_threshold: Number(data.mastery_threshold),
                    source_mode: data.source_mode,
                    source_exam_ids: data.source_mode === "EXAMS" ? data.source_exam_ids : [],
                    source_from_date: data.source_mode === "DATE_WINDOW" ? data.source_from_date || null : null,
                    source_to_date: data.source_mode === "DATE_WINDOW" ? data.source_to_date || null : null,
                    fallback_to_priority: data.fallback_to_priority,
                    assign_mode: data.assign_mode || "ALL",
                    student_ids: data.assign_mode === "SELECTED" ? data.student_ids : [],
                    notify_teachers_on_due: data.notify_teachers_on_due === true,
                    ...modePayload,
                    ...(data.question_mode !== "ADAPTIVE" ? { manual_chapters: data.manual_chapters } : {}),
                }, auth);
            } else {
                const days = cadenceNeedsDays(data.cadence) ? data.release_days_of_week : [];
                const payload = {
                    prep_program_course_id: subjectCourse.id,
                    cadence: data.cadence,
                    release_days_of_week: days,
                    release_day_of_week: days[0] ?? null, // legacy mirror; the server syncs both ways
                    release_time: data.release_time || null,
                    due_time: data.due_time || null,
                    due_offset_days: Number(data.due_offset_days),
                    num_chapters: Number(data.num_chapters),
                    questions_per_chapter: data.questions_per_chapter === "" ? null : Number(data.questions_per_chapter),
                    mastery_threshold: Number(data.mastery_threshold),
                    start_date: data.start_date || null,
                    end_date: data.end_date || null,
                    source_mode: data.source_mode,
                    source_exam_ids: data.source_mode === "EXAMS" ? data.source_exam_ids : [],
                    source_from_date: data.source_mode === "DATE_WINDOW" ? data.source_from_date || null : null,
                    source_to_date: data.source_mode === "DATE_WINDOW" ? data.source_to_date || null : null,
                    fallback_to_priority: data.fallback_to_priority,
                    assign_mode: data.assign_mode || "ALL",
                    student_ids: data.assign_mode === "SELECTED" ? data.student_ids : [],
                    notify_teachers_on_due: data.notify_teachers_on_due === true,
                    ...modePayload,
                };
                if (data.question_mode !== "ADAPTIVE") {
                    if (sameForAll) {
                        payload.manual_chapters = data.manual_chapters;
                        payload.round_plan = null; // clears a previous per-round plan on edit
                    } else {
                        payload.manual_chapters = null;
                        payload.round_plan = futureRounds.map((r) => ({
                            date: r.date,
                            manual_chapters: planByDate[r.date] || [],
                        }));
                    }
                }
                if (editRule) await Axios.put(`${base}/homework-recurrences/${editRule.id}`, payload, auth);
                else await Axios.post(`${base}/homework-recurrences`, payload, auth);
            }
            if (onSaved) onSaved();
            onClose();
        } catch (e) {
            console.error("Wizard save error:", e);
            setFormError(e.response?.data?.error || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    // ---- summaries for collapsed steps -----------------------------------------------------
    const kind = HOMEWORK_KINDS.find((k) => k.value === data.question_mode) || HOMEWORK_KINDS[0];
    const whenSummary = isRule
        ? (data.start_date && data.end_date
            ? `${cadenceSummary(data)} · ${data.start_date} → ${data.end_date}${futureRounds.length ? ` · ${futureRounds.length} round${futureRounds.length === 1 ? "" : "s"}` : ""}`
            : "")
        : (data.release_date
            ? `releases ${DateTime.fromISO(data.release_date).toFormat("EEE M/d h:mm a")}, due ${DateTime.fromISO(data.due_date || data.release_date).toFormat("EEE M/d h:mm a")}`
            : "");
    const chaptersSummary = data.question_mode === "ADAPTIVE"
        ? `${data.num_chapters} weakest chapter${Number(data.num_chapters) === 1 ? "" : "s"}, ${data.questions_per_chapter || "—"} questions each`
        : data.question_mode === "FIXED_SET"
            ? `${data.set_size}-question set · ${data.set_scope === "COHORT" ? "same for the class" : "different per student"}${isRule && !sameForAll ? " · per-round chapters" : (picksLabel(data.manual_chapters) ? ` · ${picksLabel(data.manual_chapters)}` : "")}`
            : `${isRule && !sameForAll ? "per-round chapters" : picksLabel(data.manual_chapters) || "no chapters yet"} · target ${data.questions_per_chapter || "—"}/chapter`;
    const whoSummary = `${data.assign_mode === "SELECTED" ? `${(data.student_ids || []).length} selected student${(data.student_ids || []).length === 1 ? "" : "s"}` : "all enrolled students"}${data.notify_teachers_on_due ? " · teachers get the most-missed digest" : ""}`;

    const submitLabel = !isRule
        ? `Add round — releases ${data.release_date ? DateTime.fromISO(data.release_date).toFormat("EEE M/d h:mm a") : "…"}`
        : editRule
            ? `Save rule — regenerates future rounds (${cadenceSummary(data)})`
            : `Generate ${futureRounds.length || "…"} round${futureRounds.length === 1 ? "" : "s"} — ${cadenceSummary(data)}`;

    // ---- per-round grid ---------------------------------------------------------------------
    const openRowEditor = (date) => {
        setRowDraft((planByDate[date] || []).map((p) => ({ ...p })));
        setRowEditor(date);
    };
    const commitRowEditor = () => {
        setPlanByDate((prev) => ({ ...prev, [rowEditor]: rowDraft }));
        setRowEditor(null);
    };
    const copyDown = (date) => {
        const idx = futureRounds.findIndex((r) => r.date === date);
        const next = futureRounds[idx + 1];
        if (!next) return;
        setPlanByDate((prev) => ({ ...prev, [next.date]: (prev[date] || []).map((p) => ({ ...p })) }));
    };
    const copyToAll = (date) => {
        setPlanByDate((prev) => {
            const src = (prev[date] || []).map((p) => ({ ...p }));
            const out = { ...prev };
            futureRounds.forEach((r) => { out[r.date] = src.map((p) => ({ ...p })); });
            return out;
        });
    };

    const stepBtns = (backable, nextOk, isLast = false) => (
        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            {backable && <Button size="small" onClick={() => setStep((s) => s - 1)}>Back</Button>}
            {!isLast && (
                <Button size="small" variant="contained" disabled={!nextOk} onClick={() => setStep((s) => s + 1)}>
                    Continue
                </Button>
            )}
        </Box>
    );

    const stepLabel = (title, summary, i) => (
        <StepButton onClick={() => step > i && setStep(i)} disabled={step <= i}
            optional={step > i && summary ? <Typography variant="caption" color="text.secondary">{summary}</Typography> : null}>
            {title}
        </StepButton>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {!isRule ? "Add homework round" : editRule ? "Edit recurring rounds" : "Set up recurring rounds"}
            </DialogTitle>
            <DialogContent dividers>
                {editRule && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Saving re-generates this rule's future rounds — hand-edits to those rounds are
                        discarded. Released rounds are kept.
                    </Alert>
                )}
                <Stepper activeStep={step} orientation="vertical" nonLinear>
                    {/* ── 1. What kind of homework ─────────────────────────────────────── */}
                    <Step completed={step > 0}>
                        {stepLabel("What kind of homework?", kind.title, 0)}
                        <StepContent>
                            <RadioGroup value={data.question_mode} onChange={(e) => set({ question_mode: e.target.value })}>
                                {HOMEWORK_KINDS.map((k) => (
                                    <Paper key={k.value} variant="outlined"
                                        sx={{ p: 1.25, mb: 1, borderColor: data.question_mode === k.value ? "primary.main" : "divider", cursor: "pointer" }}
                                        onClick={() => set({ question_mode: k.value })}>
                                        <FormControlLabel
                                            value={k.value}
                                            control={<Radio size="small" sx={{ pt: 0.25 }} />}
                                            sx={{ alignItems: "flex-start", width: "100%", m: 0 }}
                                            label={
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>{k.title}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{k.desc}</Typography>
                                                </Box>
                                            }
                                        />
                                    </Paper>
                                ))}
                            </RadioGroup>
                            {stepBtns(false, true)}
                        </StepContent>
                    </Step>

                    {/* ── 2. When ──────────────────────────────────────────────────────── */}
                    <Step completed={step > 1}>
                        {stepLabel("When?", whenSummary, 1)}
                        <StepContent>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                All times are ET (America/New_York). Students are emailed at the release time.
                            </Typography>
                            {!isRule ? (
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <TextField label="Release" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
                                        value={data.release_date} onChange={(e) => set({ release_date: e.target.value })} />
                                    <TextField label="Due" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
                                        value={data.due_date} onChange={(e) => set({ due_date: e.target.value })} />
                                </Box>
                            ) : (
                                <>
                                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                        <FormControl size="small" sx={{ width: 150 }}>
                                            <InputLabel>How often</InputLabel>
                                            <Select label="How often" value={data.cadence} onChange={(e) => set({ cadence: e.target.value })}>
                                                {CADENCES.map((c) => (
                                                    <MenuItem key={c} value={c}>
                                                        {CADENCE_LABELS[c] || c}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField label="Release time" type="time" size="small" sx={{ width: 140 }} InputLabelProps={{ shrink: true }}
                                            value={data.release_time} onChange={(e) => set({ release_time: e.target.value })} />
                                    </Box>
                                    {cadenceNeedsDays(data.cadence) && (
                                        <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
                                            {DOW.map((d, i) => {
                                                const on = (data.release_days_of_week || []).includes(i);
                                                return (
                                                    <Chip key={i} label={d} size="small" clickable
                                                        color={on ? "primary" : "default"} variant={on ? "filled" : "outlined"}
                                                        onClick={() => set({
                                                            release_days_of_week: on
                                                                ? data.release_days_of_week.filter((x) => x !== i)
                                                                : [...data.release_days_of_week, i].sort((a, b) => a - b),
                                                        })} />
                                                );
                                            })}
                                        </Box>
                                    )}
                                    <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
                                        <Typography variant="body2">Each round is due</Typography>
                                        <TextField type="number" size="small" sx={{ width: 80 }}
                                            value={data.due_offset_days} onChange={(e) => set({ due_offset_days: e.target.value })} />
                                        <Typography variant="body2">day(s) later at</Typography>
                                        <TextField type="time" size="small" sx={{ width: 130 }} InputLabelProps={{ shrink: true }}
                                            value={data.due_time} onChange={(e) => set({ due_time: e.target.value })} />
                                    </Box>
                                    <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 1.5 }}>
                                        <TextField label="First round on/after" type="date" size="small" InputLabelProps={{ shrink: true }}
                                            value={data.start_date} onChange={(e) => set({ start_date: e.target.value })} />
                                        <TextField label="Last round by" type="date" size="small" InputLabelProps={{ shrink: true }}
                                            value={data.end_date} onChange={(e) => set({ end_date: e.target.value })} />
                                    </Box>
                                    {preview.warning && (
                                        <StarvationWarning
                                            text={preview.warning}
                                            cadence={data.cadence}
                                            dueOffsetDays={data.due_offset_days}
                                            chaptersPerRound={data.num_chapters}
                                        />
                                    )}
                                    {preview.over_cap ? (
                                        <Alert severity="error" sx={{ mt: 1.5 }}>
                                            This rule would create more than {MAX_ROUNDS} rounds — shorten the window.
                                        </Alert>
                                    ) : futureRounds.length > 0 ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                            Creates <b>{futureRounds.length}</b> round{futureRounds.length === 1 ? "" : "s"} —
                                            {" "}first: {futureRounds.slice(0, 4).map((r) => DateTime.fromJSDate(new Date(r.release_at)).setZone("America/New_York").toFormat("EEE M/d h:mma")).join(", ")}
                                            {futureRounds.length > 4 ? ` … last: ${DateTime.fromJSDate(new Date(futureRounds[futureRounds.length - 1].release_at)).setZone("America/New_York").toFormat("EEE M/d h:mma")}` : ""}
                                        </Typography>
                                    ) : (data.start_date && data.end_date && !preview.loading ? (
                                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                                            No upcoming rounds in this window — check the dates and release days.
                                        </Alert>
                                    ) : null)}
                                </>
                            )}
                            {stepBtns(true, scheduleOk)}
                        </StepContent>
                    </Step>

                    {/* ── 3. Which chapters / questions ────────────────────────────────── */}
                    <Step completed={step > 2}>
                        {stepLabel(data.question_mode === "ADAPTIVE" ? "How much per round?" : "Which chapters?", chaptersSummary, 2)}
                        <StepContent>
                            {data.question_mode === "ADAPTIVE" && (
                                <>
                                    <Box display="flex" gap={2} flexWrap="wrap">
                                        <TextField label="Weak chapters per round" type="number" size="small" sx={{ width: 190 }}
                                            value={data.num_chapters} onChange={(e) => set({ num_chapters: e.target.value })} />
                                        <TextField label="Questions per chapter" type="number" size="small" sx={{ width: 180 }}
                                            value={data.questions_per_chapter} onChange={(e) => set({ questions_per_chapter: e.target.value })} />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                        The system picks each student's weakest chapters automatically — nothing else to choose.
                                    </Typography>
                                </>
                            )}

                            {data.question_mode === "FIXED_SET" && (
                                <Box sx={{ mb: 1.5 }}>
                                    <TextField label="Questions in the set" type="number" size="small" sx={{ width: 170 }}
                                        value={data.set_size} onChange={(e) => set({ set_size: e.target.value })} />
                                    <RadioGroup value={data.set_scope} onChange={(e) => set({ set_scope: e.target.value })} sx={{ mt: 1 }}>
                                        <FormControlLabel value="COHORT" control={<Radio size="small" />}
                                            label={<Typography variant="body2">Same set for the whole class — enables the teacher wrong-question report</Typography>} />
                                        <FormControlLabel value="PER_STUDENT" control={<Radio size="small" />}
                                            label={<Typography variant="body2">Different questions per student</Typography>} />
                                    </RadioGroup>
                                    {isRule && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            Every round deals FRESH questions (no repeats across rounds) until a chapter's pool
                                            runs out — that's the finish line, not an error.
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {data.question_mode === "MANUAL_TARGET" && (
                                <Box sx={{ mb: 1.5 }}>
                                    <TextField label="Target questions per chapter" type="number" size="small" sx={{ width: 210 }}
                                        value={data.questions_per_chapter} onChange={(e) => set({ questions_per_chapter: e.target.value })}
                                        helperText="Each picked sub-chapter can override this" />
                                </Box>
                            )}

                            {data.question_mode !== "ADAPTIVE" && isRule && (
                                <RadioGroup row value={sameForAll ? "same" : "per_round"}
                                    onChange={(e) => setSameForAll(e.target.value === "same")} sx={{ mb: 1 }}>
                                    <FormControlLabel value="same" control={<Radio size="small" />}
                                        label={<Typography variant="body2">Same chapters every round</Typography>} />
                                    <FormControlLabel value="per_round" control={<Radio size="small" />}
                                        label={<Typography variant="body2">Choose chapters for each round</Typography>} />
                                </RadioGroup>
                            )}

                            {data.question_mode !== "ADAPTIVE" && (!isRule || sameForAll) && (
                                <ManualChapterPicker
                                    qbType={qbType}
                                    subjectId={subjectCourse.ett_qb_subject_id}
                                    value={data.manual_chapters}
                                    onChange={(v) => set({ manual_chapters: v })}
                                    allowParents={data.question_mode === "FIXED_SET"}
                                    showTargets={data.question_mode === "MANUAL_TARGET"}
                                />
                            )}

                            {data.question_mode !== "ADAPTIVE" && isRule && !sameForAll && (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                        Set each round's chapters. Repeating a chapter is fine — every round
                                        automatically gets different questions from it. ⧉ copies a round's
                                        chapters to the next round.
                                    </Typography>
                                    <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Round</TableCell>
                                                <TableCell>Chapters</TableCell>
                                                <TableCell align="right"></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {futureRounds.map((r, i) => {
                                                const picks = planByDate[r.date] || [];
                                                return (
                                                    <TableRow key={r.date}>
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            {DateTime.fromISO(r.date).toFormat("EEE M/d")}
                                                        </TableCell>
                                                        <TableCell>
                                                            {picks.length ? (
                                                                <Typography variant="body2">{picksLabel(picks)}</Typography>
                                                            ) : (
                                                                <Chip size="small" color="warning" variant="outlined" label="no chapters yet" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                            <Button size="small" onClick={() => openRowEditor(r.date)}>
                                                                {picks.length ? "Edit" : "Pick"}
                                                            </Button>
                                                            {picks.length > 0 && i < futureRounds.length - 1 && (
                                                                <Tooltip title="Copy these chapters to the next round">
                                                                    <IconButton size="small" onClick={() => copyDown(r.date)} aria-label="Copy to next round">
                                                                        <ContentCopyIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            {picks.length > 0 && futureRounds.length > 1 && (
                                                                <Button size="small" onClick={() => copyToAll(r.date)}>All</Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                    {!gridComplete && (
                                        <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                                            ⚠ Every round needs chapters before the rule can be saved.
                                        </Typography>
                                    )}
                                </>
                            )}

                            <Accordion sx={{ mt: 2, boxShadow: "none", border: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="body2">Advanced</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Box display="flex" gap={2} flexWrap="wrap">
                                            {data.question_mode === "ADAPTIVE" && (
                                                <TextField label="Practiced threshold" type="number" size="small" sx={{ width: 170 }}
                                                    value={data.mastery_threshold} onChange={(e) => set({ mastery_threshold: e.target.value })}
                                                    helperText="Skip chapters practiced this many times" />
                                            )}
                                            <TextField label="Max passes" type="number" size="small" sx={{ width: 150 }}
                                                value={data.mastery_cycles} onChange={(e) => set({ mastery_cycles: e.target.value })}
                                                helperText="Small chapters retire after this many full passes" />
                                        </Box>
                                        {data.question_mode === "ADAPTIVE" && (
                                            <>
                                                <WeaknessSourceField value={data} onChange={set} programExams={programExams} />
                                                <FormControlLabel
                                                    control={<Checkbox checked={data.fallback_to_priority} onChange={(e) => set({ fallback_to_priority: e.target.checked })} />}
                                                    label="Fall back to chapter priorities when no weakness data"
                                                />
                                            </>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>

                            {stepBtns(true, chaptersOk)}
                        </StepContent>
                    </Step>

                    {/* ── 4. Who & review ──────────────────────────────────────────────── */}
                    <Step completed={false}>
                        {stepLabel("Who gets it?", whoSummary, 3)}
                        <StepContent>
                            <AssignToField
                                programUuid={programUuid}
                                rosterParams={{ program_course_id: subjectCourse.id }}
                                value={{ assign_mode: data.assign_mode, student_ids: data.student_ids }}
                                onChange={(v) => set(v)}
                            />
                            <FormControlLabel sx={{ mt: 1 }}
                                control={<Checkbox checked={data.notify_teachers_on_due} onChange={(e) => set({ notify_teachers_on_due: e.target.checked })} />}
                                label="Email teachers the class's most-missed questions when each round is due"
                            />
                            <Alert severity="info" icon={false} sx={{ mt: 1.5 }}>
                                <b>Review:</b> {kind.title} · {whenSummary || "—"} · {chaptersSummary} · {whoSummary}.
                            </Alert>
                            {formError && <Alert severity="error" sx={{ mt: 1.5 }}>{formError}</Alert>}
                            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                                <Button size="small" onClick={() => setStep(2)}>Back</Button>
                                <Button size="small" variant="contained" onClick={save} disabled={busy || !whoOk || !scheduleOk || !chaptersOk}>
                                    {submitLabel}
                                </Button>
                            </Box>
                        </StepContent>
                    </Step>
                </Stepper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>

            {/* Per-round chapter picker — staged; Save commits, Cancel discards. */}
            <Dialog open={!!rowEditor} onClose={() => setRowEditor(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Chapters for {rowEditor ? DateTime.fromISO(rowEditor).toFormat("EEEE, MMM d") : ""}
                </DialogTitle>
                <DialogContent dividers>
                    <ManualChapterPicker
                        qbType={qbType}
                        subjectId={subjectCourse.ett_qb_subject_id}
                        value={rowDraft}
                        onChange={setRowDraft}
                        allowParents={data.question_mode === "FIXED_SET"}
                        showTargets={data.question_mode === "MANUAL_TARGET"}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRowEditor(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!rowDraft.length} onClick={commitRowEditor}>
                        Save chapters{rowDraft.length ? ` (${rowDraft.length})` : ""}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default RoundWizard;
